import { UseQueryOptions } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import useAppQuery from 'hooks/useAppQuery';
import {
  GetPropertyMetadataResponseTO,
  PropertyMetadata,
} from 'models/Properties';
import API from 'services/API';

export const PROPERTY_METADATA_BASE_QUERY_KEY = 'property-metadata';

type ReactQueryOptions = Omit<
  UseQueryOptions<PropertyMetadata>,
  'queryKey' | 'queryFn'
>;

type UseFetchPropertyMetadataArgs = {
  propertyUid: string;
} & ReactQueryOptions;

const useFetchPropertyMetadata = ({
  propertyUid,
  enabled = true,
  ...options
}: UseFetchPropertyMetadataArgs) => {
  return useAppQuery<PropertyMetadata>({
    queryKey: [PROPERTY_METADATA_BASE_QUERY_KEY, propertyUid],
    queryFn: async () => {
      const response = await API.get<GetPropertyMetadataResponseTO>(
        `/v3/property-metadata/${propertyUid}`,
      );

      const { pricingMetadata, availabilityMetadata } =
        response.data.propertyMetadata;

      return {
        propertyUid,
        pricingMetadata: {
          hostfullyMostRecentUpdateDate:
            pricingMetadata.hostfullyMostRecentUpdateDate
              ? parseISO(pricingMetadata.hostfullyMostRecentUpdateDate)
              : null,
          airbnbMostRecentUpdateDate: pricingMetadata.airbnbMostRecentUpdateDate
            ? parseISO(pricingMetadata.airbnbMostRecentUpdateDate)
            : null,
          bookingComMostRecentUpdateDate:
            pricingMetadata.bookingComMostRecentUpdateDate
              ? parseISO(pricingMetadata.bookingComMostRecentUpdateDate)
              : null,
          vrboMostRecentUpdateDate: pricingMetadata.vrboMostRecentUpdateDate
            ? parseISO(pricingMetadata.vrboMostRecentUpdateDate)
            : null,
          pricingToolMostRecentUpdateDate:
            pricingMetadata.pricingToolMostRecentUpdateDate
              ? parseISO(pricingMetadata.pricingToolMostRecentUpdateDate)
              : null,
        },
        availabilityMetadata: {
          hostfullyMostRecentUpdateDate:
            availabilityMetadata.hostfullyMostRecentUpdateDate
              ? parseISO(availabilityMetadata.hostfullyMostRecentUpdateDate)
              : null,
          airbnbMostRecentUpdateDate:
            availabilityMetadata.airbnbMostRecentUpdateDate
              ? parseISO(availabilityMetadata.airbnbMostRecentUpdateDate)
              : null,
          bookingComMostRecentUpdateDate:
            availabilityMetadata.bookingComMostRecentUpdateDate
              ? parseISO(availabilityMetadata.bookingComMostRecentUpdateDate)
              : null,
        },
      } satisfies PropertyMetadata;
    },
    enabled,
    staleTime: 30000,
    keepPreviousData: true,
    ...options,
  });
};

export default useFetchPropertyMetadata;
